/**
 * Categories, brands, partners, services and site-wide settings — Firestore.
 *
 * These are the small, slow-changing collections that the header, footer, mega
 * menu and every page depend on, so they are memoised aggressively and always
 * have a bundled fallback: a failed read here would otherwise take out the
 * site chrome, not just one page.
 */

import { collection, doc, query, where, orderBy } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { CATEGORIES as FALLBACK_CATEGORIES } from "@/data/categories";
import { BRANDS as FALLBACK_BRANDS } from "@/data/brands";
import { PARTNERS as FALLBACK_PARTNERS } from "@/data/partners";
import { SERVICES as FALLBACK_SERVICES, WHY_ATHENA as FALLBACK_WHY } from "@/data/services";
import { SITE as FALLBACK_SITE } from "@/data/siteSettings";
import { cached, safe, fetchAll, fetchOne } from "./_base";

/** Shared loader for the "list of active things, in display order" pattern. */
const activeCollection = (name) =>
  fetchAll(
    query(collection(db, name), where("active", "==", true), orderBy("sortOrder", "asc"))
  );

// -------------------------------------------------------------- categories

export const toCategory = (row) => ({
  id: row.id,
  name: row.name || "",
  slug: row.slug || "",
  brandSlug: row.brandSlug || "",
  image: row.image || "",
  shortDescription: row.shortDescription || "",
  description: row.description || "",
  subcategories: Array.isArray(row.subcategories) ? row.subcategories : [],
});

const loadCategories = () =>
  cached("categories:all", () =>
    safe(
      async () => (await activeCollection("categories")).map(toCategory),
      FALLBACK_CATEGORIES,
      "categories.getCategories"
    )
  );

export const categoriesService = {
  getCategories: () => loadCategories(),
  getCategoryBySlug: async (slug) =>
    (await loadCategories()).find((category) => category.slug === slug) || null,
  /** Flat list of every subcategory, tagged with its parent — used by filters. */
  getSubcategories: async () =>
    (await loadCategories()).flatMap((category) =>
      category.subcategories.map((sub) => ({ ...sub, categorySlug: category.slug }))
    ),
};

// ------------------------------------------------------------------ brands

export const toBrand = (row) => ({
  id: row.id,
  name: row.name || "",
  slug: row.slug || "",
  fullName: row.fullName || row.name || "",
  website: row.website || "",
  description: row.description || "",
  logo: row.logo || null,
  categories: Array.isArray(row.categories) ? row.categories : [],
});

const loadBrands = () =>
  cached("brands:all", () =>
    safe(
      async () => (await activeCollection("brands")).map(toBrand),
      FALLBACK_BRANDS,
      "brands.getBrands"
    )
  );

export const brandsService = {
  getBrands: () => loadBrands(),
  getBrandBySlug: async (slug) => (await loadBrands()).find((brand) => brand.slug === slug) || null,
};

// ---------------------------------------------------------------- partners

export const toPartner = (row) => ({
  id: row.id,
  name: row.name || "",
  slug: row.slug || "",
  brandSlug: row.brandSlug || row.slug || "",
  website: row.website || "",
  logo: row.logo || null,
  description: row.description || "",
  hasProducts: row.hasProducts === true,
});

const loadPartners = () =>
  cached("partners:all", () =>
    safe(
      async () => (await activeCollection("partners")).map(toPartner),
      FALLBACK_PARTNERS,
      "partners.getPartners"
    )
  );

export const partnersService = {
  getPartners: () => loadPartners(),
  getPartnerBySlug: async (slug) =>
    (await loadPartners()).find((partner) => partner.slug === slug) || null,
};

// ---------------------------------------------------------------- services

export const toService = (row) => ({
  id: row.id,
  name: row.name || "",
  slug: row.slug || "",
  icon: row.icon || "PackageCheck",
  short: row.short || "",
  hero: row.hero || "",
  highlights: Array.isArray(row.highlights) ? row.highlights : [],
  body: row.body || "",
  images: Array.isArray(row.images) ? row.images : [],
});

const loadServices = () =>
  cached("services:all", () =>
    safe(
      async () => (await activeCollection("services")).map(toService),
      FALLBACK_SERVICES,
      "services.getServices"
    )
  );

export const servicesService = {
  getServices: () => loadServices(),
  getServiceBySlug: async (slug) =>
    (await loadServices()).find((service) => service.slug === slug) || null,
};

// --------------------------------------------------------------- customers

export const toCustomer = (row) => ({
  id: row.id,
  name: row.name || "",
  logo: row.logo || "",
  website: row.website || "",
});

export const customersService = {
  getCustomers: () =>
    cached("customers:all", () =>
      safe(
        async () => (await activeCollection("customers")).map(toCustomer),
        // No bundled fallback: an empty customer list is a legitimate state
        // (none added yet), and the home page falls back to its text band.
        [],
        "customers.getCustomers"
      )
    ),
};

// ------------------------------------------------------------- legal pages

export const toLegalPage = (row) => ({
  id: row.id,
  slug: row.slug || row.id || "",
  title: row.title || "",
  lastUpdated: row.lastUpdated || "",
  content: Array.isArray(row.content) ? row.content : [],
});

export const legalService = {
  /**
   * Privacy Policy and Terms of Use.
   *
   * Deliberately not given a bundled fallback: these run to several thousand
   * words each and would sit in the main bundle for every visitor, on pages
   * almost nobody opens. The page shows a short notice instead if the read
   * fails, which is the honest thing to display when we cannot be sure the
   * legal text is current.
   */
  getLegalPage: (slug) =>
    cached(`legal:${slug}`, () =>
      safe(
        async () => {
          const row = await fetchOne(doc(db, "legalPages", slug));
          return row ? toLegalPage(row) : null;
        },
        null,
        `legal.getLegalPage(${slug})`
      )
    ),
};

// ----------------------------------------------------------- site settings

/**
 * Merge the stored settings over the bundled defaults so a partially filled
 * settings document can never blank out the footer's contact details.
 */
export const toSiteSettings = (row) => {
  if (!row) return FALLBACK_SITE;
  return {
    ...FALLBACK_SITE,
    ...row,
    whatsapp: { ...FALLBACK_SITE.whatsapp, ...(row.whatsapp || {}) },
    social: { ...FALLBACK_SITE.social, ...(row.social || {}) },
    logos: { ...FALLBACK_SITE.logos, ...(row.logos || {}) },
  };
};

export const siteSettingsService = {
  getSiteSettings: () =>
    cached("settings:site", () =>
      safe(
        async () => toSiteSettings(await fetchOne(doc(db, "settings", "site"))),
        FALLBACK_SITE,
        "settings.getSiteSettings"
      )
    ),

  /** Head / body code injection managed in the CMS. */
  getCodeInjection: () =>
    cached("settings:codeInjection", () =>
      safe(
        async () => {
          const row = await fetchOne(doc(db, "settings", "codeInjection"));
          return {
            head: row?.head || "",
            bodyStart: row?.bodyStart || "",
            bodyEnd: row?.bodyEnd || "",
            enabled: row?.enabled !== false,
          };
        },
        { head: "", bodyStart: "", bodyEnd: "", enabled: true },
        "settings.getCodeInjection"
      )
    ),

  /** Editable "Why Athena Scientific" feature list shown on the home page. */
  getWhyAthena: () =>
    cached("settings:whyAthena", () =>
      safe(
        async () => {
          const row = await fetchOne(doc(db, "settings", "whyAthena"));
          return Array.isArray(row?.items) && row.items.length ? row.items : FALLBACK_WHY;
        },
        FALLBACK_WHY,
        "settings.getWhyAthena"
      )
    ),
};

// -------------------------------------------------------------- SEO pages

/**
 * Per-route SEO overrides. Keyed by a slugified route ("home", "about",
 * "products", "contact"…) so the CMS can manage them as a simple list.
 */
export const seoService = {
  getSeoPages: () =>
    cached("seo:all", () =>
      safe(async () => fetchAll(collection(db, "seoPages")), [], "seo.getSeoPages")
    ),

  getSeoForRoute: async (routeKey) => {
    if (!routeKey) return null;
    const pages = await seoService.getSeoPages();
    return pages.find((page) => page.id === routeKey || page.route === routeKey) || null;
  },
};
